import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'

import Button from '@instabox/internal-web-ui/components/Button'
import PageHeader from '@instabox/internal-web-ui/components/PageHeader'
import PageLoading from '@instabox/internal-web-ui/components/PageLoading'
import Tooltip from '@instabox/internal-web-ui/components/Tooltip'
import ResetIcon from '@instabox/internal-web-ui/icons/ResetIcon'
import notify from '@instabox/internal-web-ui/utils/notify'
import { BaselinePackage } from '@instabox/lockers-update-common/types'
import { format } from 'date-fns'

import fetch from '../../fetch'
import useCurrentRollout from '../../hooks/useCurrentRollout'
import usePackages from '../../hooks/usePackages'
import useProcessBaselineChanges from '../../hooks/useProcessBaselineChanges'
import {
  filterPackagesInBaseline,
  filterPackagesNotInBaseline,
  prepBaselineCandidate,
  sortPackages,
} from '../../utils/baseline'

import BaselinePackagesTable from './BaselinePackagesTable'
import ConfirmationModal from './ConfirmationModal'

interface SaveBaselineValidation {
  valid: boolean
  message: string
}

const BaselineElement = (): ReactElement => {
  // Fixed data i.e. current baseline in DB and existing packages in Gemfury
  const { packages: gemfuryPackages, loading: packagesLoading, reload: reloadPackages } = usePackages()
  const { currentRollout: currentBaseline, loading: baselineLoading, reload: reloadBaseline } = useCurrentRollout(true)

  const [isLoading, setLoading] = useState<boolean>(true)
  useEffect(() => setLoading(packagesLoading || baselineLoading), [packagesLoading, baselineLoading])

  const createdAt = currentBaseline?.createdAt
    ? format(new Date(currentBaseline?.createdAt), 'yyyy-LL-dd hh:mm:ss')
    : null

  // Editable data i.e. packages shown in each table that can move between them to set the (future) new baseline
  const [baselinePackages, setBaselinePackages] = useState<BaselinePackage[]>([])
  const [availablePackages, setAvailablePackages] = useState<BaselinePackage[]>([])

  const currentBaselinePackages = useMemo(() => currentBaseline?.packages || [], [currentBaseline?.packages])
  const groupPackages = useCallback(() => {
    setBaselinePackages(filterPackagesInBaseline(currentBaselinePackages, gemfuryPackages))
    setAvailablePackages(filterPackagesNotInBaseline(currentBaselinePackages, gemfuryPackages))
  }, [currentBaselinePackages, gemfuryPackages])

  useEffect(() => groupPackages(), [currentBaseline, gemfuryPackages, groupPackages])

  const onVersionChange = (packageName: string, newVersion: string) => {
    const newBaselinePackages = baselinePackages.map((pkg) => {
      if (pkg.name !== packageName) {
        return pkg
      }

      return {
        ...pkg,
        selectedVersion: newVersion,
      }
    })
    setBaselinePackages(newBaselinePackages)
  }

  const onTogglePackage = (pkg: BaselinePackage) => {
    const isPackageInBaseline = !!baselinePackages.find(({ name }) => name === pkg.name)

    if (isPackageInBaseline) {
      setBaselinePackages(baselinePackages.filter(({ name }) => name !== pkg.name))
      setAvailablePackages([...availablePackages, pkg].sort(sortPackages))
    } else {
      setAvailablePackages(availablePackages.filter(({ name }) => name !== pkg.name))
      setBaselinePackages([...baselinePackages, pkg].sort(sortPackages))
    }
  }

  // Modal
  const [showModal, setShowModal] = useState<boolean>(false)
  const onCloseModal = () => setShowModal(false)
  const { totalChanges, processedBaseline } = useProcessBaselineChanges(currentBaselinePackages, baselinePackages)

  // "Establish new baseline" button
  const onEstablishNew = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setShowModal(true)
  }
  const allPackagesHaveSelectedVersion = useMemo(
    () => baselinePackages.reduce((acc, { selectedVersion }) => acc && !!selectedVersion, true),
    [baselinePackages]
  )
  const validateChanges = (): SaveBaselineValidation => {
    if (!totalChanges) {
      return {
        valid: false,
        message: 'No changes were made to the baseline',
      }
    }

    if (!allPackagesHaveSelectedVersion) {
      return {
        valid: false,
        message: 'One or more packages does not have a version selected',
      }
    }

    return {
      valid: true,
      message: '',
    }
  }

  const renderSaveButton = () => {
    const { valid, message } = validateChanges()

    const saveButton = (
      <Button
        data-testid="rolloutSubmitButton"
        size="lg"
        type="submit"
        disabled={!valid}
        onClick={(e) => onEstablishNew(e)}
      >
        Establish new baseline
      </Button>
    )

    if (!valid) {
      return <Tooltip content={message}>{saveButton}</Tooltip>
    }

    return saveButton
  }

  const onConfirmEstablishNewBaseline = async () => {
    if (!totalChanges) {
      notify({
        type: 'info',
        message: 'No changes were made to the current baseline',
        theme: 'colored',
      })
      return
    }

    setLoading(true)
    setShowModal(false)

    const response = await fetch('/api/rollout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(prepBaselineCandidate(baselinePackages)),
    })

    setLoading(false)

    if (response.ok === false) {
      notify({
        type: 'error',
        message: `Failed to establish new baseline: ${response.status} ${response.statusText}`,
        theme: 'colored',
      })
      // Intentional console.error for easier debugging should an error occur
      console.error(response)
      return
    }

    notify({
      type: 'success',
      message: 'New baseline established!',
      theme: 'colored',
    })
    reloadBaseline()
  }

  if (isLoading) {
    return <PageLoading title="Current Baseline" />
  }

  return (
    <form>
      <PageHeader
        title={currentBaseline ? `Current Baseline #${currentBaseline?.rolloutId}` : 'Create a baseline'}
        description={currentBaseline && `Created at ${createdAt}`}
        buttons={[
          {
            text: 'Refresh Packages',
            icon: <ResetIcon />,
            iconPosition: 'right',
            variant: 'light',
            shape: 'outlined',
            onClick: () => reloadPackages(true, true),
          },
        ]}
      />

      <PageHeader title="Packages in baseline" tag="h2" />
      <BaselinePackagesTable
        isLoading={isLoading}
        packages={baselinePackages}
        inBaseline={true}
        onTogglePackage={onTogglePackage}
        onVersionChange={onVersionChange}
      />

      <div className="row my-3">
        <div className="col" style={{ margin: '-20px 0 20px' }}>
          {renderSaveButton()}
        </div>
      </div>

      <PageHeader title="Available packages" tag="h2" />
      <BaselinePackagesTable
        isLoading={isLoading}
        packages={availablePackages}
        inBaseline={false}
        onTogglePackage={onTogglePackage}
      />

      <ConfirmationModal
        show={showModal}
        onCancel={onCloseModal}
        onConfirm={() => onConfirmEstablishNewBaseline()}
        totalChanges={totalChanges}
        processedBaseline={processedBaseline}
        newBaselinePkgs={baselinePackages}
      />
    </form>
  )
}

export default BaselineElement
